import React, { useMemo } from 'react'
import { NavLink, useRouteMatch } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import logo from '../assets/logo.svg'
import profile from '../assets/profile.svg'

import { makeStyles } from '@material-ui/core/styles'
import { AppBar, Avatar, Box, Container, Tab, Tabs, Toolbar } from '@material-ui/core'

import { UserSyncWarning } from './UserSyncWarning'
import { ImpersonationBar } from './ImpersonationBar'
import { StoreMatchingWarningBar } from './StoreMatchingWarningBar'
import { MoySkladAppTrialNotificationBar } from './MoySkladAppTrialNotificationBar'
import { WbApiTokensExpirationNotificationBars } from './WbApiTokensExpirationNotificationBars'
import { YandexMarketNewApiKeyFormatNotificationBars } from './YandexMarketNewApiKeyFormatNotificationBars'

import { useStore } from '../hooks/storeHook'
import { useIntegrationRoute } from '../hooks/routeHooks'

import { appRoutes } from '../common/appRoutes'

import { integrationKeyByType, IntegrationTypeKey } from '../types/integrationTypeUtils'

import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'

interface TabInfo {
  label: string

  integration: IntegrationTypeKey

  route?: string
}

export const Header =
  observer(() => {
    const classes = useStyles()

    const store = useStore()

    const {integration} = useIntegrationRoute()

    const isAccountRoute = !!useRouteMatch(appRoutes.Account)
    const isMoySkladRoute = !!useRouteMatch(appRoutes.MoySklad.account)

    const integrationTypeKey = integration ? integrationKeyByType[integration]
                                           : undefined

    const tabs: TabInfo[] = useMemo(() => {
        const allowedTabs: TabInfo[] = []

        if (store.allowedIntegrations[IntegrationType.Ozon]) {
          allowedTabs.push(
            {
              label: 'OZON',
              integration: 'Ozon',
              route: appRoutes.Store.products.makeUrl(
                {accountId: store.syncStore.ozonAccounts[0]?.id ?? 0, integration: 'Ozon'})
            })
        }

        if (store.allowedIntegrations[IntegrationType.Wildberries]) {
          allowedTabs.push(
            {
              label: 'WILDBERRIES',
              integration: 'Wildberries',
              route: appRoutes.Store.products.makeUrl(
                {accountId: store.syncStore.wbAccounts[0]?.id ?? 0, integration: 'Wildberries'})
            })
        }

        if (store.allowedIntegrations[IntegrationType.YandexMarket]) {
          allowedTabs.push(
            {
              label: 'Я.МАРКЕТ',
              integration: 'YandexMarket',
              route: appRoutes.Store.products.makeUrl(
                {accountId: store.syncStore.yandexMarketAccounts[0]?.id ?? 0, integration: 'YandexMarket'})
            })
        }

        allowedTabs.push({
          label: 'МОЙСКЛАД',
          integration: 'MoySklad',
          route: appRoutes.MoySklad.account
        })

        return allowedTabs
      },
      [store.allowedIntegrations,
        store.syncStore.ozonAccounts,
        store.syncStore.wbAccounts,
        store.syncStore.yandexMarketAccounts])

    const tabIndex = tabs.findIndex(_ => _.integration === integrationTypeKey
                                         || (isMoySkladRoute && _.integration === 'MoySklad'))

    return (
      <AppBar position="static" className={classes.appBar}>
        <Container maxWidth={false}>
          <Toolbar>
            <Box display="flex" justifyContent="space-around" alignItems="center" width="100%">
              <Box>
                <ImpersonationBar/>
                <UserSyncWarning/>
                <MoySkladAppTrialNotificationBar/>
                <WbApiTokensExpirationNotificationBars/>
                <YandexMarketNewApiKeyFormatNotificationBars/>
                <StoreMatchingWarningBar/>
              </Box>

              <NavLink to="/">
                <Box className="logo_container">
                  <img src={logo} alt="Logo"/>
                </Box>
              </NavLink>

              <Tabs
                value={tabIndex >= 0 ? tabIndex : false}
                className="navigation"
                classes={{indicator: classes.tabIndicator}}
              >
                {
                  tabs.map((tab, index) =>
                    <Tab
                      key={index}
                      disableRipple
                      label={tab.label}
                      component={NavLink}
                      to={tab.route ?? ''}
                      className={clsx(
                        'nav_link',
                        classes.tab,
                        index === tabIndex && classes.navLinkUnderlined
                      )}
                    />)
                }
              </Tabs>

              <Box className="user_account">
                <NavLink to={appRoutes.Account} title="Аккаунт MPsklad">
                  <Avatar
                    variant="rounded"
                    alt="Profile"
                    src={profile}
                    className={clsx(classes.avatar, isAccountRoute && classes.avatarActive)}
                    classes={{img: classes.avatarImg}}
                  />
                </NavLink>
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    )
  })

const useStyles =
  makeStyles(({palette}) => ({
    appBar: {
      color: palette.mpSkladSecondary.darkBlue1,
      background: palette.mpSkladPrimary.white,
      borderBottom: '1px solid #DFE4EB'
    },
    tab: {
      minWidth: 50,
      marginRight: 40,
      '& span': {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '19.2px'
      },
      '&.MuiTab-textColorInherit': {
        opacity: '1'
      }
    },
    tabIndicator: {
      display: 'none'
    },
    navLinkUnderlined: {
      '& span': {
        fontSize: 16,
        fontWeight: 700,
        lineHeight: '19.2px',
        display: 'inline-block',
        color: palette.mpSkladPrimary.blue,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '-1px',
          height: '2px',
          backgroundColor: palette.mpSkladPrimary.blue,
          display: 'block',
          width: '100%'
        }
      }
    },
    avatar: {
      width: 30,
      height: 32,
      borderBottom: '2px solid transparent',
      borderRadius: 0
    },
    avatarActive: {
      borderBottomColor: palette.mpSkladPrimary.blue
    },
    avatarImg: {
      width: 30,
      height: 30
    }
  }))